import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { monthRange, downloadCsv, downloadPdf, fmt, toastExportNeedsLoad } from '../reportUtils';
import { useTenantCompanyName, ReportToolbar, ReportDocument, fmtRp } from '../ReportShell';
import { COA_TYPE_LABELS } from '../accountingUiConstants';

function periodText(start, end) {
  if (start && end) return `Untuk periode ${start} s.d. ${end}`;
  if (end) return `Per ${end}`;
  return '';
}

function typeLabel(t) {
  return COA_TYPE_LABELS[t] || t || '—';
}

export default function ReportTrialBalance() {
  const company = useTenantCompanyName();
  const { end: mEnd } = monthRange();
  const [tbStart, setTbStart] = useState('');
  const [tbEnd, setTbEnd] = useState(mEnd);
  const [tb, setTb] = useState(null);

  useEffect(() => {
    setTb(null);
  }, [tbStart, tbEnd]);

  const loadTb = () => {
    const q = { end: tbEnd };
    if (tbStart) q.start = tbStart;
    api.get('/tenant/accounting/trial-balance', { params: q }).then((r) => setTb(r.data)).catch(() => toast.error('Gagal trial balance'));
  };

  const hasRange = Boolean(tb?.start);
  const exportParams = () => {
    const p = { end: tbEnd };
    if (tbStart) p.start = tbStart;
    return p;
  };

  const doCsv = () => {
    if (!tb) return toastExportNeedsLoad();
    downloadCsv('/tenant/accounting/trial-balance', exportParams(), 'neraca_saldo.csv');
  };
  const doPdf = () => {
    if (!tb) return toastExportNeedsLoad();
    downloadPdf('/tenant/accounting/trial-balance', exportParams(), 'neraca_saldo.pdf');
  };

  return (
    <div>
      <ReportToolbar>
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-slate-600">Mulai (opsional)</Label>
          <Input type="date" value={tbStart} onChange={(e) => setTbStart(e.target.value)} className="h-9 w-[150px]" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-slate-600">Sampai</Label>
          <Input type="date" value={tbEnd} onChange={(e) => setTbEnd(e.target.value)} className="h-9 w-[150px]" />
        </div>
        <Button type="button" onClick={loadTb} className="h-9">
          Muat data
        </Button>
        <Button type="button" variant="outline" className="h-9" onClick={doCsv}>
          <Download className="mr-1 h-4 w-4" />
          CSV
        </Button>
        <Button type="button" variant="outline" className="h-9" onClick={doPdf}>
          PDF
        </Button>
      </ReportToolbar>

      {tb && (
        <ReportDocument
          companyName={company}
          title="LAPORAN NERACA SALDO (TRIAL BALANCE)"
          subtitle="Ringkasan saldo akun menurut buku besar"
          periodLabel={periodText(tb.start, tb.end)}
        >
          <table className="acct-rpt-table">
            <thead>
              <tr>
                <th>Kode</th>
                <th>Nama akun</th>
                <th>Tipe</th>
                {hasRange ? (
                  <>
                    <th className="acct-rpt-th-num">Saldo awal</th>
                    <th className="acct-rpt-th-num">Mutasi D</th>
                    <th className="acct-rpt-th-num">Mutasi K</th>
                    <th className="acct-rpt-th-num">Saldo akhir</th>
                  </>
                ) : (
                  <>
                    <th className="acct-rpt-th-num">Debit</th>
                    <th className="acct-rpt-th-num">Kredit</th>
                    <th className="acct-rpt-th-num">Saldo</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {tb.lines?.map((row, i) => (
                <tr key={i}>
                  <td className="acct-rpt-code">{row.code}</td>
                  <td>{row.name}</td>
                  <td className="text-xs text-slate-600">{typeLabel(row.type)}</td>
                  {hasRange ? (
                    <>
                      <td className="acct-rpt-num">{fmtRp(row.opening_balance)}</td>
                      <td className="acct-rpt-num">{fmt(row.period_debit)}</td>
                      <td className="acct-rpt-num">{fmt(row.period_credit)}</td>
                      <td className="acct-rpt-num font-semibold">{fmtRp(row.closing_balance)}</td>
                    </>
                  ) : (
                    <>
                      <td className="acct-rpt-num">{fmtRp(row.debit)}</td>
                      <td className="acct-rpt-num">{fmtRp(row.credit)}</td>
                      <td className="acct-rpt-num font-semibold">{fmtRp(row.balance)}</td>
                    </>
                  )}
                </tr>
              ))}
              <tr className="font-bold">
                <td colSpan={3} className="text-right uppercase tracking-wide" style={{ borderTop: '2px solid #0f172a' }}>
                  Jumlah
                </td>
                {hasRange ? (
                  <>
                    <td className="acct-rpt-num" style={{ borderTop: '2px solid #0f172a' }} />
                    <td className="acct-rpt-num" style={{ borderTop: '2px solid #0f172a' }}>
                      {fmtRp(tb.total_debit)}
                    </td>
                    <td className="acct-rpt-num" style={{ borderTop: '2px solid #0f172a' }}>
                      {fmtRp(tb.total_credit)}
                    </td>
                    <td className="acct-rpt-num" style={{ borderTop: '2px solid #0f172a' }} />
                  </>
                ) : (
                  <>
                    <td className="acct-rpt-num" style={{ borderTop: '2px solid #0f172a' }}>
                      {fmtRp(tb.total_debit)}
                    </td>
                    <td className="acct-rpt-num" style={{ borderTop: '2px solid #0f172a' }}>
                      {fmtRp(tb.total_credit)}
                    </td>
                    <td className="acct-rpt-num" style={{ borderTop: '2px solid #0f172a' }} />
                  </>
                )}
              </tr>
            </tbody>
          </table>
          <p className="acct-rpt-summary-bar mt-4">
            <strong>Catatan:</strong> Neraca saldo memverifikasi bahwa total debit dan kredit dalam periode konsisten dengan pembukuan berpasangan.
          </p>
        </ReportDocument>
      )}

      {!tb && <div className="acct-rpt-empty">Pilih tanggal lalu klik <strong>Muat data</strong> untuk menampilkan laporan.</div>}
    </div>
  );
}
